import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Box,
  SxProps,
  Theme,
} from '@mui/material';
import { siteLanguagesService, getLanguageName } from 'components/api/siteLanguages';
import { SupportedLanguage } from 'types/siteLanguage';
import { commonSelectSx } from 'styles/muiStyles';

interface SupportedLanguageSelectProps {
  value: string;
  onChange: (value: string, selectedLang?: SupportedLanguage) => void;
  languages?: SupportedLanguage[];
  label?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  className?: string;
  sx?: SxProps<Theme>;
}

const SupportedLanguageSelect: React.FC<SupportedLanguageSelectProps> = ({
  value,
  onChange,
  languages,
  label = 'Supported Language',
  disabled = false,
  allowCustom = true,
  fullWidth = true,
  size = 'small',
  className = '',
  sx,
}) => {
  const [items, setItems] = useState<SupportedLanguage[]>(languages || []);
  const [loading, setLoading] = useState(!languages || languages.length === 0);

  useEffect(() => {
    if (languages && languages.length > 0) {
      setItems(languages);
      setLoading(false);
      return;
    }

    let isMounted = true;
    const fetchSupported = async () => {
      try {
        setLoading(true);
        const list = await siteLanguagesService.getSupportedLanguages();
        if (isMounted) {
          setItems(list);
        }
      } catch (error) {
        console.error('Failed to load supported languages:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSupported();

    return () => {
      isMounted = false;
    };
  }, [languages]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    const matched = items.find((item) => item.code === val);
    onChange(val, matched);
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} className={className}>
      <InputLabel
        sx={{
          color: '#8698a6',
          '&.Mui-focused': {
            color: 'var(--accent-blue)',
          },
        }}
      >
        {label}
      </InputLabel>
      <Select
        value={value}
        label={label}
        onChange={handleChange}
        disabled={disabled || loading}
        sx={[
          ...(Array.isArray(commonSelectSx) ? commonSelectSx : [commonSelectSx]),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        MenuProps={{
          PaperProps: {
            style: { maxHeight: 300 },
            sx: {
              backgroundColor: 'var(--bg-panel)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
            },
          },
        }}
        endAdornment={
          loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <CircularProgress size={16} sx={{ color: 'var(--text-secondary)' }} />
            </Box>
          ) : null
        }
      >
        {items.map((item) => {
          const nativeStr =
            item.nativeName && item.nativeName.toLowerCase() !== item.name.toLowerCase()
              ? ` (${item.nativeName})`
              : '';
          return (
            <MenuItem key={item.code} value={item.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {item.code.toUpperCase()}
                </span>
                <span>—</span>
                <span>
                  {item.name || getLanguageName(item.code)}
                  {nativeStr}
                </span>
              </Box>
            </MenuItem>
          );
        })}
        {allowCustom && (
          <MenuItem value="__custom__" sx={{ fontStyle: 'italic', color: 'var(--accent-blue)' }}>
            + Enter custom language code
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default SupportedLanguageSelect;
